<!-- eslint-disable vue/no-v-model-argument -->
<template>
  <AntRow class="activity-log">
    <AntCol :span="24">
      <AntAlert
        v-if="loadError"
        type="error"
        showIcon
        style="margin-bottom: 16px;"
        :message="`讀取 Activity Log 失敗：${loadError}`"
      />

      <AntRow :gutter="[12, 12]" class="activity-log-filters" align="middle">
        <AntCol :span="5">
          <AntRangePicker
            v-model:value="filters.dateRange"
            style="width: 100%;"
            :placeholder="['Start Date', 'End Date']"
            allowClear
            :disabledDate="disabledDate"
            @change="onFilterChange"
          />
        </AntCol>
        <AntCol :span="3">
          <AntSelect
            v-model:value="filters.editorUsername"
            style="width: 100%;"
            placeholder="Editor"
            allowClear
            :options="editorOptions"
            @change="onFilterChange"
          />
        </AntCol>
        <AntCol :span="3">
          <AntSelect
            v-model:value="filters.operation"
            style="width: 100%;"
            placeholder="Operation"
            allowClear
            :options="operationOptions"
            @change="onFilterChange"
          />
        </AntCol>
        <AntCol :span="4">
          <AntSelect
            v-model:value="filters.target"
            style="width: 100%;"
            placeholder="Target"
            allowClear
            showSearch
            optionFilterProp="label"
            :options="targetOptions"
            @change="onTargetChange"
          />
        </AntCol>
        <AntCol :span="9">
          <AntInputSearch
            v-model:value="keywordDraft"
            allowClear
            @search="onKeywordSearch"
          />
        </AntCol>
      </AntRow>

      <div
        ref="tableWrapRef"
        class="activity-log-body"
      >
        <AntTable
          size="middle"
          :columns="columns"
          :dataSource="pagedEntries"
          :pagination="pagination"
          :loading="loading"
          :rowClassName="rowClassName"
          :scroll="{ y: tableScrollY }"
          @change="onTableChange"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.dataIndex === 'operation'">
              {{ getOperationGroupMeta(record.action).label }}
            </template>
            <template v-else-if="column.dataIndex === 'at'">
              {{ formatAt(record.at) }}
            </template>
            <template v-else-if="column.dataIndex === 'editorUsername'">
              <SearchHighlightText :text="record.editorUsername" :keyword="filters.keyword" />
            </template>
            <template v-else-if="column.dataIndex === 'target'">
              <div class="activity-log-target">
                <template v-if="record.targetModel.showMaster">
                  <div class="activity-log-target__headline">
                    <SearchHighlightText text="[母本]" :keyword="filters.keyword" />
                  </div>
                  <div
                    v-for="label in record.targetModel.masterLabels"
                    :key="label"
                    class="activity-log-target__line"
                  >
                    <SearchHighlightText :text="label" :keyword="filters.keyword" />
                  </div>
                  <template v-if="record.targetModel.affectedDocs.length">
                    <div class="activity-log-target__headline">
                      <SearchHighlightText text="[影響子本]" :keyword="filters.keyword" />
                    </div>
                    <div
                      v-for="doc in record.targetModel.affectedDocs"
                      :key="doc.docId"
                      class="activity-log-target__line"
                    >
                      <SearchHighlightText :text="doc.docShortTitle" :keyword="filters.keyword" />
                    </div>
                  </template>
                </template>
                <SearchHighlightText
                  v-else
                  :text="record.targetModel.primary"
                  :keyword="filters.keyword"
                />
              </div>
            </template>
            <template v-else-if="column.dataIndex === 'detail'">
              <ActivityLogChangeDetail
                :key="record.id"
                :entry="record"
                :keyword="filters.keyword"
                :docsDataByDocId="docsDataByDocId"
                :sectionsByWallet="sectionsByWallet"
                :entirePlatformsMap="entirePlatformsMap"
                @open-details="onOpenDetails(record)"
              />
            </template>
            <template v-else-if="column.dataIndex === 'remark'">
              <div
                class="activity-log-remark-cell"
                @click.stop
              >
                <AntTextarea
                  v-if="editingRemarkId === record.id"
                  v-model:value="editingRemarkValue"
                  :rows="3"
                  allowClear
                  autoFocus
                  :disabled="savingRemark"
                />
                <div
                  v-else
                  class="activity-log-remark-text"
                >
                  <SearchHighlightText :text="record.remark || ''" :keyword="filters.keyword" />
                </div>
                <div class="activity-log-remark-actions">
                  <template v-if="editingRemarkId === record.id">
                    <SaveIcon
                      title="Save"
                      @click="onSaveRemark(record)"
                    />
                    <CancelIcon
                      title="Cancel"
                      @click="onCancelRemark"
                    />
                  </template>
                  <EditIcon
                    v-else
                    title="Edit Remark"
                    @click="onEditRemark(record)"
                  />
                </div>
              </div>
            </template>
          </template>
        </AntTable>
      </div>

      <AntDrawer
        wrapClassName="activity-log-detail-drawer"
        :visible="drawerVisible"
        :width="drawerHasReleaseNotes ? '90%' : '60%'"
        :destroyOnClose="true"
        @close="onCloseDrawer"
        @afterVisibleChange="onDrawerAfterVisibleChange"
      >
        <template #title>
          <div v-if="selectedEntry">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div>
                {{ getActivityLogDrawerTitle(selectedEntry) }}
              </div>
              <div style="color: rgba(0, 0, 0, .45); font-size: 15px; display: flex; gap: 12px;">
                <span>Editor: {{ selectedEntry.editorUsername }}</span>
                <span>{{ formatAt(selectedEntry.at) }}</span>
              </div>
            </div>
          </div>
        </template>

        <template v-if="selectedEntry">
          <div
            v-if="selectedEntry.detail?.publishedDocsDataByDocId"
            class="activity-log-draft-change"
          >
            <DraftChange
              :key="selectedEntry.id"
              readOnly
              type="publish"
              :highlightKeyword="(filters.keyword || '').trim()"
              :snapshotPublishedDocsDataByDocId="expandedSelectedPublishedDocs"
              :docsDataByDocId="publishDocsDataByDocId"
              :sectionsByWallet="sectionsByWallet"
              :entirePlatformsMap="entirePlatformsMap"
            />
          </div>
          <div v-else class="activity-log-empty">
            此發佈紀錄缺少 publishedDocsDataByDocId 快照
          </div>
        </template>
      </AntDrawer>
    </AntCol>
  </AntRow>
</template>

<script>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'

import dayjs from 'dayjs'
import moment from 'moment'

import {
  Row as AntRow,
  Col as AntCol,
  Table as AntTable,
  Select as AntSelect,
  Input as AntInput,
  Alert as AntAlert,
  DatePicker as AntDatePicker,
  Drawer as AntDrawer,
  message as antMessage,
} from 'ant-design-vue'

import EditIcon from '@ant-design/icons-vue/lib/icons/EditOutlined'
import SaveIcon from '@ant-design/icons-vue/lib/icons/CheckOutlined'
import CancelIcon from '@ant-design/icons-vue/lib/icons/CloseOutlined'

import { getActivityLogs, updateActivityLogRemark } from '@/api/apiDocV3'

import {
  docsDataFromPublishedDocs,
  expandPublishedDocsFromActivityLog,
} from '@/components/ApiDoc/publish/publishDiff'

import DraftChange from '@/components/ApiDoc/publish/DraftChange.vue'
import ActivityLogChangeDetail from '@/components/ApiDoc/management/ActivityLog/ActivityLogChangeDetail.vue'
import SearchHighlightText from '@/components/ApiDoc/shared/SearchHighlightText.vue'

import {
  ACTIVITY_ACTIONS,
  ACTIVITY_LOG_RETENTION_DAYS,
  ACTIVITY_OPERATION_GROUP_LIST,
  getOperationGroupMeta,
} from './constants'
import {
  activityLogMatchesTargetFilter,
  getActivityLogDrawerTitle,
  getActivityLogTargetFilterValues,
  getActivityLogTargetModel,
} from './utils'

export default {
  name: 'ActivityLogPanel',
  components: {
    AntRow,
    AntCol,
    AntTable,
    AntSelect,
    AntInputSearch: AntInput.Search,
    AntAlert,
    AntRangePicker: AntDatePicker.RangePicker,
    AntDrawer,
    AntTextarea: AntInput.TextArea,
    EditIcon,
    SaveIcon,
    CancelIcon,
    DraftChange,
    SearchHighlightText,
    ActivityLogChangeDetail,
  },
  props: {
    docsDataByDocId: { type: Object, default: () => ({}) },
    sectionsByWallet: { type: Object, default: () => ({}) },
    entirePlatformsMap: { type: Object, default: () => ({}) },
  },
  setup(props) {
    const filters = reactive({
      dateRange: null,
      editorUsername: undefined,
      operation: undefined,
      target: undefined,
      /** 已套用的搜尋字（按 Enter／點搜尋後才更新） */
      keyword: '',
    })
    /** 搜尋框草稿，打字時不觸發後端 */
    const keywordDraft = ref('')
    const selectedEntry = ref(null)
    const drawerVisible = ref(false)
    const remoteEntries = ref([])
    const loading = ref(false)
    const loadError = ref('')
    const tableWrapRef = ref(null)
    const tableScrollY = ref(360)
    const editingRemarkId = ref(null)
    const editingRemarkValue = ref('')
    const savingRemark = ref(false)
    let tableResizeObserver = null
    let clearHighlightOnPointer = null

    const removeClearHighlightListener = () => {
      if (!clearHighlightOnPointer) return
      document.removeEventListener('pointerdown', clearHighlightOnPointer, true)
      clearHighlightOnPointer = null
    }

    const updateTableScrollY = () => {
      const el = tableWrapRef.value
      if (!el) return
      const paginationEl = el.querySelector('.ant-pagination')
      const headerEl = el.querySelector('.ant-table-header') || el.querySelector('.ant-table-thead')
      const paginationH = paginationEl?.offsetHeight || 56
      const headerH = headerEl?.offsetHeight || 39
      tableScrollY.value = Math.max(160, el.clientHeight - paginationH - headerH)
    }
    const pagination = reactive({
      current: 1,
      pageSize: 20,
      total: 0,
      showSizeChanger: true,
      showTotal: total => `共 ${total} 筆`,
    })

    const fetchEntries = async () => {
      loading.value = true
      loadError.value = ''
      try {
        const params = { limit: 500 }
        if (filters.dateRange?.length === 2) {
          params.from = moment(dayjs(filters.dateRange[0]).toDate()).startOf('day').toISOString(true)
          params.to = moment(dayjs(filters.dateRange[1]).toDate()).endOf('day').toISOString(true)
        }
        if (filters.editorUsername) params.editorUsername = filters.editorUsername
        if (filters.operation) params.operation = filters.operation
        if (filters.keyword?.trim()) params.keyword = filters.keyword.trim()

        const res = await getActivityLogs(params)
        if (Array.isArray(res?.data)) {
          remoteEntries.value = res.data
        } else {
          throw new Error(res?.message || 'invalid response')
        }
      } catch (err) {
        loadError.value = err?.message || String(err)
        remoteEntries.value = []
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      fetchEntries()
      nextTick(() => {
        updateTableScrollY()
        if (typeof ResizeObserver === 'undefined' || !tableWrapRef.value) return
        tableResizeObserver = new ResizeObserver(() => updateTableScrollY())
        tableResizeObserver.observe(tableWrapRef.value)
      })
    })

    onBeforeUnmount(() => {
      removeClearHighlightListener()
      if (tableResizeObserver) tableResizeObserver.disconnect()
      tableResizeObserver = null
    })

    const retentionCutoff = computed(() => dayjs().subtract(ACTIVITY_LOG_RETENTION_DAYS, 'day').startOf('day'))

    const filteredEntries = computed(() => (
      remoteEntries.value
        .filter((entry) => {
          const at = dayjs(entry.at)
          if (!at.isValid() || at.isBefore(retentionCutoff.value)) return false
          if (!activityLogMatchesTargetFilter(entry, filters.target, props.docsDataByDocId)) return false
          return true
        })
        .sort((a, b) => dayjs(b.at).valueOf() - dayjs(a.at).valueOf())
    ))

    const isPublishAction = entry => (
      entry?.action === ACTIVITY_ACTIONS.PUBLISH
      || ['publish', 'publish_master'].includes(entry?.detail?.kind)
    )

    const pagedEntries = computed(() => {
      const start = (pagination.current - 1) * pagination.pageSize
      const highlightedId = selectedEntry.value?.id
      return filteredEntries.value.slice(start, start + pagination.pageSize).map(e => ({
        ...e,
        key: e.id,
        targetModel: getActivityLogTargetModel(e, props.docsDataByDocId),
        highlighted: !!(highlightedId && e.id === highlightedId && isPublishAction(e)),
      }))
    })

    watch([loading, pagedEntries], () => nextTick(updateTableScrollY))

    watch(filteredEntries, (list) => {
      pagination.total = list.length
    }, { immediate: true })

    const editorOptions = computed(() => {
      const names = [...new Set(remoteEntries.value.map(e => e.editorUsername).filter(Boolean))]
      return names.sort().map(value => ({ value, label: value }))
    })

    const operationOptions = computed(() => ACTIVITY_OPERATION_GROUP_LIST.map(g => ({
      value: g.key,
      label: g.label,
    })))

    const targetOptions = computed(() => {
      const labels = [...new Set(
        remoteEntries.value.flatMap(entry => (
          getActivityLogTargetFilterValues(entry, props.docsDataByDocId)
        )),
      )]
      const sorted = labels
        .sort((a, b) => {
          const aWallet = a.endsWith(' Wallet') ? 0 : 1
          const bWallet = b.endsWith(' Wallet') ? 0 : 1
          if (aWallet !== bWallet) return aWallet - bWallet
          return a.localeCompare(b)
        })
        .map(value => ({ value, label: value }))
      return [{ value: '—', label: '—' }, ...sorted]
    })

    const disabledDate = (current) => {
      if (!current) return false
      const today = dayjs().endOf('day')
      const cutoff = dayjs().subtract(ACTIVITY_LOG_RETENTION_DAYS, 'day').startOf('day')
      return current.isAfter(today) || current.isBefore(cutoff)
    }

    const columns = [
      {
        title: 'Time', dataIndex: 'at', key: 'at', width: 120,
      },
      {
        title: 'Editor', dataIndex: 'editorUsername', key: 'editorUsername', width: 80,
      },
      {
        title: 'Operation', dataIndex: 'operation', key: 'operation', width: 100,
      },
      {
        title: 'Target', dataIndex: 'target', key: 'target', width: 240,
      },
      {
        title: 'Detail', dataIndex: 'detail', key: 'detail', minWidth: 250, className: 'activity-log-col-detail',
      },
      {
        title: 'Remark', dataIndex: 'remark', key: 'remark', width: 200, className: 'activity-log-col-remark',
      },
    ]

    const expandedSelectedPublishedDocs = computed(() => (
      expandPublishedDocsFromActivityLog(selectedEntry.value?.detail?.publishedDocsDataByDocId)
    ))

    /** 發佈詳情：publishedDocsDataByDocId[docId].docData */
    const publishDocsDataByDocId = computed(() => {
      const detail = selectedEntry.value?.detail
      if (!detail) return props.docsDataByDocId
      return docsDataFromPublishedDocs(
        expandedSelectedPublishedDocs.value,
        props.docsDataByDocId,
      )
    })

    const formatAt = at => dayjs(at).format('YYYY/MM/DD HH:mm')

    const drawerHasReleaseNotes = computed(() => {
      const published = selectedEntry.value?.detail?.publishedDocsDataByDocId || {}
      return Object.values(published).some(entry => entry?.shouldRelease)
    })

    const resetListView = () => {
      removeClearHighlightListener()
      pagination.current = 1
      drawerVisible.value = false
      selectedEntry.value = null
    }

    const onFilterChange = () => {
      resetListView()
      fetchEntries()
    }

    const onTargetChange = () => {
      resetListView()
    }

    /** 僅 Enter／點搜尋圖示／清空後確認時套用 keyword */
    const onKeywordSearch = (value) => {
      const next = String(value ?? keywordDraft.value ?? '').trim()
      keywordDraft.value = next
      filters.keyword = next
      onFilterChange()
    }

    const onTableChange = (pag) => {
      pagination.current = pag.current
      pagination.pageSize = pag.pageSize
    }

    const scheduleClearHighlightOnNextPointer = () => {
      removeClearHighlightListener()
      const onPointerDown = () => {
        removeClearHighlightListener()
        selectedEntry.value = null
      }
      clearHighlightOnPointer = onPointerDown
      window.setTimeout(() => {
        if (clearHighlightOnPointer !== onPointerDown) return
        document.addEventListener('pointerdown', onPointerDown, true)
      }, 0)
    }

    const onCloseDrawer = () => {
      drawerVisible.value = false
      scheduleClearHighlightOnNextPointer()
    }

    const onDrawerAfterVisibleChange = (open) => {
      if (open) {
        removeClearHighlightListener()
        return
      }
      scheduleClearHighlightOnNextPointer()
    }

    const onOpenDetails = (record) => {
      if (selectedEntry.value?.id === record.id && drawerVisible.value) {
        drawerVisible.value = false
        scheduleClearHighlightOnNextPointer()
        return
      }
      removeClearHighlightListener()
      selectedEntry.value = record
      drawerVisible.value = true
    }

    const rowClassName = record => (record.highlighted ? 'activity-log-row--selected' : '')

    const onEditRemark = (record) => {
      editingRemarkId.value = record.id
      editingRemarkValue.value = record.remark || ''
    }

    const onCancelRemark = () => {
      editingRemarkId.value = null
      editingRemarkValue.value = ''
    }

    const onSaveRemark = async (record) => {
      if (savingRemark.value) return
      const nextRemark = String(editingRemarkValue.value ?? '').trim()
      const prevRemark = String(record.remark || '').trim()
      if (nextRemark === prevRemark) {
        onCancelRemark()
        return
      }
      savingRemark.value = true
      try {
        const { data, message } = await updateActivityLogRemark({
          id: record.id,
          at: record.at,
          remark: nextRemark,
        })
        if (!data) {
          antMessage.error(`Update Remark failed: ${message || 'unknown error'}`)
          return
        }
        const idx = remoteEntries.value.findIndex(entry => entry.id === record.id)
        if (idx >= 0) {
          const next = { ...remoteEntries.value[idx] }
          if (data.remark) next.remark = data.remark
          else delete next.remark
          remoteEntries.value.splice(idx, 1, next)
        }
        onCancelRemark()
        antMessage.success('Update Remark successfully')
      } catch (err) {
        antMessage.error(`Update Remark failed: ${err.message || err}`)
      } finally {
        savingRemark.value = false
      }
    }

    return {
      filters,
      keywordDraft,
      selectedEntry,
      drawerVisible,
      drawerHasReleaseNotes,
      onCloseDrawer,
      onDrawerAfterVisibleChange,
      pagination,
      pagedEntries,
      editorOptions,
      operationOptions,
      targetOptions,
      disabledDate,
      columns,
      isPublishAction,
      publishDocsDataByDocId,
      expandedSelectedPublishedDocs,
      getOperationGroupMeta,
      getActivityLogDrawerTitle,
      formatAt,
      onFilterChange,
      onTargetChange,
      onKeywordSearch,
      onTableChange,
      onOpenDetails,
      rowClassName,
      editingRemarkId,
      editingRemarkValue,
      savingRemark,
      onEditRemark,
      onCancelRemark,
      onSaveRemark,
      loadError,
      loading,
      tableWrapRef,
      tableScrollY,
    }
  },
}
</script>

<style scoped>
.activity-log {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.activity-log > .ant-col {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.activity-log > .ant-col > .ant-alert {
  flex-shrink: 0;
}

.activity-log-filters {
  margin-bottom: 16px;
  flex-shrink: 0;
}

.activity-log-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.activity-log-body :deep(.ant-table-wrapper),
.activity-log-body :deep(.ant-spin-nested-loading),
.activity-log-body :deep(.ant-spin-container) {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.activity-log-body :deep(.ant-table) {
  width: 100%;
  flex: 1;
  min-height: 0;
}

.activity-log-body :deep(.ant-table table) {
  table-layout: fixed;
  width: 100% !important;
}

.activity-log-body :deep(.ant-pagination) {
  flex-shrink: 0;
  margin-bottom: 0;
}

.activity-log-body :deep(.ant-table-tbody > tr > td) {
  vertical-align: top;
}

.activity-log-body :deep(.ant-table-thead > tr > th.activity-log-col-detail),
.activity-log-body :deep(.ant-table-tbody > tr > td.activity-log-col-detail) {
  min-width: 250px;
  overflow: hidden;
}

.activity-log-body :deep(.ant-table-thead > tr > th.activity-log-col-remark),
.activity-log-body :deep(.ant-table-tbody > tr > td.activity-log-col-remark) {
  width: 200px !important;
}

.activity-log-remark-cell {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.activity-log-remark-cell :deep(.ant-input-textarea) {
  flex: 1;
  min-width: 0;
}

.activity-log-remark-text {
  flex: 1;
  min-width: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.activity-log-remark-actions {
  flex-shrink: 0;
  display: flex;
  gap: 6px;
  padding-top: 2px;
}

.activity-log-remark-actions :deep(.anticon) {
  cursor: pointer;
  font-size: 16px;
}

.activity-log-remark-actions :deep(.anticon:hover) {
  color: #1890ff;
}

.activity-log-target {
  font-size: 13px;
  line-height: 1.5;
  white-space: normal;
  word-break: break-word;
}

.activity-log-target__headline {
  margin-top: 8px;
}

.activity-log-target__headline:first-child {
  margin-top: 0;
}

.activity-log-target__line {
  min-width: 0;
}

.activity-log-empty {
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
}

:deep(.activity-log-row--selected) > td {
  background: #e6f7ff !important;
}
</style>

<style>
.activity-log-detail-drawer:not(.ant-drawer-open) {
  display: none;
}

.activity-log-detail-drawer .ant-drawer-content-wrapper {
  overflow: hidden;
}

.activity-log-detail-drawer .ant-drawer-wrapper-body {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.activity-log-detail-drawer .ant-drawer-header {
  flex-shrink: 0;
}

.activity-log-detail-drawer .ant-drawer-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
}

.activity-log-detail-drawer .activity-log-draft-change {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: 4px;
}

.activity-log-detail-drawer .search-highlight {
  background-color: #ffe58f;
  padding: 0;
}
</style>
